/**
 * Unified Catalog Configuration
 * Single source of truth for bouquet sizes and catalog items.
 */

// ===========================================
// BOUQUET SIZES CONFIG
// ===========================================

export interface BouquetSizeConfig {
  maxStems: number;
  label: string;
  description: string;
}

export const BOUQUET_SIZES: Record<string, BouquetSizeConfig> = {
  small: {
    maxStems: 15,
    label: 'קטן',
    description: 'עד 15 ענפים',
  },
  medium: {
    maxStems: 25,
    label: 'בינוני',
    description: 'עד 25 ענפים',
  },
  large: {
    maxStems: 35,
    label: 'גדול',
    description: 'עד 35 ענפים',
  },
};

export type BouquetSizeKey = keyof typeof BOUQUET_SIZES;

// ===========================================
// CATALOG ITEMS CONFIG
// ===========================================

// Support both English (legacy) and Hebrew (new) category names
export type ItemCategory = 'flower' | 'green' | 'filler' | 'addon' | 'wrap' | 'פרחים' | 'ממלאי נפח' | 'עטיפות' | 'תוספות';

export interface CatalogItemConfig {
  id: string;
  displayNameHe: string;
  category: ItemCategory;
  bundleStems: number;
  pricePerBundle: number;
  imagePath: string; // Thumbnail for card
  stemVariations?: string[]; // Array of 3 stem variations for preview [v1, v2, v3]
  defaultScale?: number;
  radiusHint?: number;
  enabled: boolean;
  stock: number; // Number of bundles available
  color?: string; // Fallback color if image not available
}

export const CATALOG_ITEMS: CatalogItemConfig[] = [
  // Flowers - פרחים
  {
    id: 'shoshan',
    displayNameHe: 'שושן',
    category: 'flower',
    bundleStems: 5,
    pricePerBundle: 50,
    imagePath: '/flowers/shoshan.png',
    defaultScale: 1.0,
    radiusHint: 24,
    enabled: true,
    stock: 20,
    color: '#FFFFFF',
  },
  {
    id: 'florit',
    displayNameHe: 'פלורית',
    category: 'flower',
    bundleStems: 5,
    pricePerBundle: 50,
    imagePath: '/flowers/florit.png',
    defaultScale: 0.9,
    radiusHint: 20,
    enabled: true,
    stock: 25,
    color: '#FFB6C1',
  },
  {
    id: 'lisianthus_pink',
    displayNameHe: 'ליזיאנטוס ורוד',
    category: 'flower',
    bundleStems: 5,
    pricePerBundle: 45,
    imagePath: '/flowers/lisianthus.png',
    defaultScale: 0.95,
    radiusHint: 22,
    enabled: true,
    stock: 30,
    color: '#FFB7C5',
  },
  {
    id: 'margarit',
    displayNameHe: 'מרגריט',
    category: 'flower',
    bundleStems: 5,
    pricePerBundle: 50,
    imagePath: '/flowers/margarit.png',
    defaultScale: 0.85,
    radiusHint: 18,
    enabled: true,
    stock: 35,
    color: '#FFFEF0',
  },
  {
    id: 'sunflower',
    displayNameHe: 'חמנייה',
    category: 'flower',
    bundleStems: 5,
    pricePerBundle: 35,
    imagePath: '/flowers/sunflower.png',
    defaultScale: 1.1,
    radiusHint: 28,
    enabled: true,
    stock: 20,
    color: '#F2D37A',
  },
  {
    id: 'gerbera',
    displayNameHe: 'גרברה',
    category: 'flower',
    bundleStems: 5,
    pricePerBundle: 35,
    imagePath: '/flowers/gerbera.png',
    defaultScale: 1.0,
    radiusHint: 24,
    enabled: true,
    stock: 25,
    color: '#E8A3A8',
  },
  {
    id: 'roses',
    displayNameHe: 'ורדים',
    category: 'flower',
    bundleStems: 10,
    pricePerBundle: 80,
    imagePath: '/flowers/rose.png',
    defaultScale: 0.9,
    radiusHint: 22,
    enabled: true,
    stock: 15,
    color: '#D8A1B2',
  },
  {
    id: 'ostomeria',
    displayNameHe: 'אוסטומריה',
    category: 'flower',
    bundleStems: 5,
    pricePerBundle: 50,
    imagePath: '/flowers/ostomeria.png',
    defaultScale: 0.85,
    radiusHint: 20,
    enabled: true,
    stock: 20,
    color: '#DA70D6',
  },
  {
    id: 'lisianthus',
    displayNameHe: 'ליזיאנטוס',
    category: 'flower',
    bundleStems: 5,
    pricePerBundle: 35,
    imagePath: '/flowers/lisianthus.png',
    defaultScale: 0.95,
    radiusHint: 22,
    enabled: true,
    stock: 30,
    color: '#E6E6FA',
  },

  // Greens - ירק
  {
    id: 'leaves',
    displayNameHe: 'עלים',
    category: 'green',
    bundleStems: 14,
    pricePerBundle: 150,
    imagePath: '/flowers/leaves.png',
    defaultScale: 1.2,
    radiusHint: 30,
    enabled: true,
    stock: 10,
    color: '#A7BFA8',
  },
  {
    id: 'eucalyptus',
    displayNameHe: 'אקליפטוס',
    category: 'green',
    bundleStems: 5,
    pricePerBundle: 30,
    imagePath: '/flowers/eucalyptus.png',
    defaultScale: 1.0,
    radiusHint: 18,
    enabled: true,
    stock: 40,
    color: '#728C69',
  },
  {
    id: 'saxons',
    displayNameHe: 'סקסונס',
    category: 'green',
    bundleStems: 5,
    pricePerBundle: 35,
    imagePath: '/flowers/saxons.png',
    defaultScale: 0.9,
    radiusHint: 16,
    enabled: true,
    stock: 30,
    color: '#355E3B',
  },
  {
    id: 'mimosa',
    displayNameHe: 'מימוזה',
    category: 'filler',
    bundleStems: 5,
    pricePerBundle: 35,
    imagePath: '/flowers/mimosa.png',
    defaultScale: 0.8,
    radiusHint: 14,
    enabled: true,
    stock: 25,
    color: '#FFE135',
  },

  // Wraps - עטיפות
  {
    id: 'wrap-kraft',
    displayNameHe: 'נייר קראפט',
    category: 'wrap',
    bundleStems: 0,
    pricePerBundle: 15,
    imagePath: '/wraps/wrap-news.jpg',
    enabled: true,
    stock: 100,
    color: '#D4A574',
  },
  {
    id: 'wrap-white',
    displayNameHe: 'נייר לבן יוקרתי',
    category: 'wrap',
    bundleStems: 0,
    pricePerBundle: 20,
    imagePath: '/wraps/wrap-white.jpg',
    enabled: true,
    stock: 100,
    color: '#FEFEFE',
  },
  {
    id: 'wrap-black',
    displayNameHe: 'נייר שחור מט',
    category: 'wrap',
    bundleStems: 0,
    pricePerBundle: 25,
    imagePath: '/wraps/wrap-black.jpg',
    enabled: true,
    stock: 100,
    color: '#3A3A3A',
  },

  // Addons - תוספות
  {
    id: 'ribbon-satin',
    displayNameHe: 'סרט סאטן',
    category: 'addon',
    bundleStems: 0,
    pricePerBundle: 8,
    imagePath: '/builder/satin-ribbon.png',
    enabled: true,
    stock: 100,
    color: '#E7C3CF',
  },
  {
    id: 'card',
    displayNameHe: 'כרטיס ברכה',
    category: 'addon',
    bundleStems: 0,
    pricePerBundle: 0,
    imagePath: '/builder/greeting-card.png',
    enabled: true,
    stock: 200,
    color: '#F3E9DF',
  },
  {
    id: 'chocolate',
    displayNameHe: 'שוקולד בלגי',
    category: 'addon',
    bundleStems: 0,
    pricePerBundle: 45,
    imagePath: '/builder/chocolate.png',
    enabled: true,
    stock: 50,
    color: '#4A3728',
  },
  {
    id: 'wine',
    displayNameHe: 'יין',
    category: 'addon',
    bundleStems: 0,
    pricePerBundle: 85,
    imagePath: '/shop/addons/addons-wine-boutique.jpg',
    enabled: true,
    stock: 30,
    color: '#722F37',
  },
  {
    id: 'teddy-bear',
    displayNameHe: 'דובי',
    category: 'addon',
    bundleStems: 0,
    pricePerBundle: 65,
    imagePath: '/builder/teddy-bear.png',
    enabled: true,
    stock: 25,
    color: '#FFFAF0',
  },
];

// ===========================================
// HELPER FUNCTIONS
// ===========================================

export function getMaxStems(size: string | null): number {
  if (!size || !BOUQUET_SIZES[size]) {
    return BOUQUET_SIZES.medium.maxStems;
  }
  return BOUQUET_SIZES[size].maxStems;
}

export function getRemainingStems(currentStems: number, maxStems: number): number {
  return Math.max(0, maxStems - currentStems);
}

export function canAddItem(currentStems: number, maxStems: number, itemBundleStems: number): boolean {
  return currentStems + itemBundleStems <= maxStems;
}

export function getCatalogItem(id: string): CatalogItemConfig | undefined {
  return CATALOG_ITEMS.find(item => item.id === id);
}

export function getCatalogByCategory(category: ItemCategory): CatalogItemConfig[] {
  return CATALOG_ITEMS.filter(item => item.category === category && item.enabled);
}

// Category labels - support both English (legacy) and Hebrew category keys
export const CATEGORY_LABELS: Record<ItemCategory, string> = {
  // Legacy English keys
  flower: 'פרחים',
  green: 'ממלאי נפח',
  filler: 'ממלאי נפח',
  wrap: 'עטיפות',
  addon: 'תוספות',
  // New Hebrew keys (from database)
  'פרחים': 'פרחים',
  'ממלאי נפח': 'ממלאי נפח',
  'עטיפות': 'עטיפות',
  'תוספות': 'תוספות',
};

// Lucide icon names for categories (used in components)
export const CATEGORY_ICON_NAMES: Record<ItemCategory, string> = {
  flower: 'Flower2',
  green: 'Leaf',
  filler: 'Sparkles',
  wrap: 'Gift',
  addon: 'Heart',
  'פרחים': 'Flower2',
  'ממלאי נפח': 'Leaf',
  'עטיפות': 'Gift',
  'תוספות': 'Heart',
};

// Legacy emoji icons (deprecated, use CATEGORY_ICON_NAMES)
export const CATEGORY_ICONS: Record<ItemCategory, string> = {
  flower: '🌸',
  green: '🌿',
  filler: '✨',
  wrap: '🎀',
  addon: '🎁',
  'פרחים': '🌸',
  'ממלאי נפח': '🌿',
  'עטיפות': '🎀',
  'תוספות': '🎁',
};
